(() => {
  if (window.WolfRecommendationHistory) return;

  const STORAGE_KEY = 'wolf:recommendation-history:v1';
  const CHANGE_EVENT = 'wolf:recommendation-history-change';
  const MAX_RECORDS = 20;
  const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  const INIT_TIMEOUT = 1500;
  let allowed = false;
  let records = [];
  let signature = '[]';
  let listening = false;
  let deniedHandled = false;
  let consentDenied = false;
  let loading = false;
  let initializing = null;
  let finishInitialization = null;

  const disabled = () => window.Shopify?.designMode === true;
  const text = (value) => typeof value === 'string' && value.length <= 128 ? value.trim().replace(/\s+/g, ' ') : '';

  function sanitizeRecord(value, now, currentView = false) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const id = typeof value.id === 'string' ? text(value.id) :
      typeof value.id === 'number' && Number.isSafeInteger(value.id) && value.id > 0 ? String(value.id) : '';
    const viewedAt = currentView ? now : value.viewedAt;
    if (!id || typeof viewedAt !== 'number' || !Number.isFinite(viewedAt) || viewedAt <= now - MAX_AGE || viewedAt > now) return null;
    const forms = [...new Set((Array.isArray(value.forms) ? value.forms : []).map(text).filter(Boolean))].slice(0, 12);
    // Persist only the fields used for local recommendation preferences.
    return { id, forms, color: text(value.color), viewedAt };
  }

  function sanitizeHistory(value) {
    if (!Array.isArray(value)) return [];
    const now = Date.now();
    const seen = new Set();
    return value.map((record) => sanitizeRecord(record, now)).filter(Boolean)
      .sort((a, b) => b.viewedAt - a.viewedAt)
      .filter((record) => {
        if (seen.has(record.id)) return false;
        seen.add(record.id);
        return true;
      }).slice(0, MAX_RECORDS);
  }

  function update(nextRecords, nextAllowed) {
    // Refreshing the timestamp of the same newest view does not change the mix.
    const nextSignature = JSON.stringify(nextRecords.map(({ id, forms, color }) => ({ id, forms, color })));
    const consentChanged = allowed !== nextAllowed;
    const changed = consentChanged || signature !== nextSignature;
    records = nextRecords;
    allowed = nextAllowed;
    signature = nextSignature;
    if (changed) document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      // Callers record the current view only on a newly permitted session.
      // Re-recording for history changes would make open tabs echo each other.
      detail: { consentChanged, preferencesAllowed: nextAllowed },
    }));
  }

  function removeStoredHistory() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (_) {
      return false;
    }
  }

  function permission() {
    if (disabled()) return false;
    try {
      if (window.WolfConsent) return window.WolfConsent.allows('preferences');
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.preferencesProcessingAllowed !== 'function') return null;
      // Even without the theme controller, regional defaults are not consent.
      return privacy.currentVisitorConsent?.().preferences === 'yes' && privacy.preferencesProcessingAllowed() === true;
    } catch (_) {
      return null;
    }
  }

  function readStoredHistory() {
    try {
      const storage = window.localStorage;
      const raw = storage.getItem(STORAGE_KEY);
      let next = [];
      try {
        next = raw && raw.length <= 100000 ? sanitizeHistory(JSON.parse(raw)) : [];
      } catch (_) {
        // Invalid or old browser data never influences recommendations.
      }
      const canonical = next.length ? JSON.stringify(next) : null;
      if (raw !== canonical) {
        try {
          if (canonical) storage.setItem(STORAGE_KEY, canonical);
          else storage.removeItem(STORAGE_KEY);
        } catch (_) {
          // A sanitized read remains safe if storage has become read-only.
        }
      }
      return { records: next, available: true };
    } catch (_) {
      return { records: [], available: false };
    }
  }

  function refresh() {
    const status = permission();
    if (disabled()) {
      update([], false);
      return { ready: true, available: false };
    }
    if (status !== true || consentDenied) {
      // Removal does not read browsing history. Clear once on denial/revocation.
      if ((status === false || consentDenied || allowed) && !deniedHandled) {
        removeStoredHistory();
        deniedHandled = true;
      }
      update([], false);
      return { ready: status !== null, available: false };
    }
    deniedHandled = false;
    const stored = readStoredHistory();
    update(stored.records, true);
    return { ready: true, available: stored.available };
  }

  function listen() {
    if (listening || disabled()) return;
    listening = true;
    document.addEventListener('wolf:consent-change', () => {
      consentDenied = !window.WolfConsent?.allows('preferences');
      refresh();
      init();
    });
    document.addEventListener('visitorConsentCollected', (event) => {
      if (typeof event.detail?.preferencesAllowed === 'boolean') consentDenied = !event.detail.preferencesAllowed;
      refresh();
      // Retry if the API or Shopify loader was not ready during initial loading.
      init();
    });
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      if (permission() !== true || consentDenied || disabled()) return;
      try {
        if (event.storageArea && event.storageArea !== window.localStorage) return;
      } catch (_) {
        return;
      }
      refresh();
    });
  }

  function loadPrivacy() {
    if (loading || typeof window.Shopify?.loadFeatures !== 'function') return;
    loading = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
        loading = false;
        if (!error && refresh().ready) finishInitialization?.();
      });
    } catch (_) {
      loading = false;
    }
  }

  function init() {
    if (disabled()) return Promise.resolve(false);
    listen();
    if (refresh().ready) return Promise.resolve(allowed);
    if (initializing) return initializing;
    const deadline = Date.now() + INIT_TIMEOUT;
    initializing = new Promise((resolve) => {
      let timer;
      let finished = false;
      let attemptedLoad = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        loading = false;
        finishInitialization = null;
        resolve(allowed);
      };
      finishInitialization = finish;
      const check = () => {
        if (refresh().ready || Date.now() >= deadline) {
          finish();
          return;
        }
        if (!attemptedLoad && typeof window.Shopify?.loadFeatures === 'function') {
          attemptedLoad = true;
          loadPrivacy();
        }
        if (!finished) timer = setTimeout(check, Math.min(50, Math.max(0, deadline - Date.now())));
      };
      check();
    });
    const result = initializing;
    result.then(() => { if (initializing === result) initializing = null; });
    return result;
  }

  function read() {
    listen();
    refresh();
    return records.map((record) => ({ ...record, forms: [...record.forms] }));
  }

  function record(value) {
    listen();
    if (!refresh().available || !allowed) return false;
    const nextRecord = sanitizeRecord(value, Date.now(), true);
    if (!nextRecord) return false;
    const next = [nextRecord, ...records.filter((item) => item.id !== nextRecord.id)].slice(0, MAX_RECORDS);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      return false;
    }
    update(next, true);
    return true;
  }

  function clear() {
    if (disabled()) return false;
    const removed = removeStoredHistory();
    update([], permission() === true && !consentDenied);
    return removed;
  }

  window.WolfRecommendationHistory = { init, read, record, clear };
})();
